import { useEffect, useMemo, useRef, useState } from "react";
import api from "../../services/api";
import {
  LIST_LIMIT, buildSeries, buildSuppliers, currentMonth, monthsBefore, monthsDescending, summarizeMonth,
} from "./dashboardData";

// A API tem limite de requisições compartilhado com o resto do app (um 429 aqui
// derruba também /auth/me e a barra de status): poucas chamadas, com pausa e cache.
const CONCURRENCY = 2;
const PAUSE_MS = 150;
const MAX_RETRIES = 3;
const MAX_MONTHS = 60;
const CACHE_TTL_MS = 10 * 60 * 1000;
// Diferença aceitável entre a soma dos meses e o total da API (0,01%)
const TOLERANCE = 0.0001;

const EMPTY_SERIES = { months: [], loading: false, complete: true, failed: false };
const ZERO_MONTH = { icms: 0, ipi: 0, pis: 0, total: 0 };

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

// Em 429, espera o que a API pedir (ou um recuo crescente) e tenta de novo
export async function withRetry(fn, isCancelled) {
  for (let attempt = 0; ; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await fn();
    } catch (err) {
      if (err.response?.status !== 429 || attempt >= MAX_RETRIES || isCancelled()) throw err;
      const retryAfter = Number(err.response.headers?.["retry-after"]);
      // eslint-disable-next-line no-await-in-loop
      await sleep(retryAfter > 0 ? retryAfter * 1000 : 4000 * 2 ** attempt);
    }
  }
}

// ─── Cache da série na sessão: voltar à tela ou recarregar não refaz as chamadas ─
const seriesKey = (project, empresa) => `vidal_creditos_series_v2:${project}|${empresa}`;

function readSeriesCache(key) {
  try {
    const cached = JSON.parse(sessionStorage.getItem(key));
    if (cached && Date.now() - cached.at < CACHE_TTL_MS) {
      return { at: cached.at, months: new Map(Object.entries(cached.months)) };
    }
  } catch { /* cache inválido: consulta de novo */ }
  return { at: Date.now(), months: new Map() };
}

function writeSeriesCache(key, months, at) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ at, months: Object.fromEntries(months) }));
  } catch { /* sem espaço: segue sem cache */ }
}

// Anos cobertos por um intervalo, do mais recente ao mais antigo
const yearRanges = (start, end) => {
  const ranges = [];
  for (let y = parseInt(end.slice(0, 4), 10); y >= parseInt(start.slice(0, 4), 10); y -= 1) {
    const from = `${y}-01` < start ? start : `${y}-01`;
    const to = `${y}-12` > end ? end : `${y}-12`;
    ranges.push({ from, to, months: monthsDescending(to, from) });
  }
  return ranges;
};

/**
 * Série mensal real: a API não tem esse agregado, então a lista é pedida por mês.
 * Para gastar menos chamadas, consulta o total de cada ano antes: ano zerado é
 * pulado inteiro e, dentro de um ano, para assim que os meses somam o total dele.
 * Termina quando a soma alcança o total informado pela API, o que também diz se a
 * série está completa.
 */
export function useMonthlySeries({ project, empresa, dataInicio, dataFim, total, ready, reloadKey }) {
  const [series, setSeries] = useState(EMPTY_SERIES);
  const lastReload = useRef(reloadKey);

  useEffect(() => {
    if (!ready || !project || total <= 0) {
      setSeries(EMPTY_SERIES);
      return undefined;
    }

    const cacheKey = seriesKey(project, empresa);
    if (lastReload.current !== reloadKey) {
      lastReload.current = reloadKey;
      sessionStorage.removeItem(cacheKey);
    }
    const { at: cachedAt, months: known } = readSeriesCache(cacheKey);

    const end = dataFim || currentMonth();
    const start = dataInicio || monthsBefore(end, MAX_MONTHS);
    const scanned = new Map();
    let cancelled = false;
    const isCancelled = () => cancelled;

    const sumOf = (months) => months.reduce((acc, key) => acc + (scanned.get(key)?.total || 0), 0);
    const reachedTotal = () => sumOf([...scanned.keys()]) >= total * (1 - TOLERANCE);

    const fetchRange = (from, to) => withRetry(async () => {
      const params = { projeto: project, limit: LIST_LIMIT, dataInicio: from, dataFim: to };
      if (empresa) params.empresa = empresa;
      const { data } = await api.get("/creditos/lista", { params });
      if (!data.success) throw new Error("Resposta sem sucesso");
      return summarizeMonth(data.creditos || []);
    }, isCancelled);

    const publish = (state) => setSeries({ months: buildSeries(scanned), ...state });

    const scanYear = async ({ from, to, months }) => {
      const missing = months.filter((key) => !known.has(key));
      let yearTotal = Infinity;
      if (missing.length > 1) {
        // eslint-disable-next-line no-await-in-loop
        yearTotal = (await fetchRange(from, to)).total;
        if (yearTotal <= 0) {
          months.forEach((key) => known.set(key, ZERO_MONTH));
          return;
        }
      }

      const pending = [...months];
      while (pending.length > 0 && sumOf(months) < yearTotal * (1 - TOLERANCE) && !reachedTotal()) {
        const batch = pending.splice(0, CONCURRENCY);
        const toFetch = batch.filter((key) => !known.has(key));
        // eslint-disable-next-line no-await-in-loop
        const results = await Promise.all(toFetch.map((key) => fetchRange(key, key)));
        if (cancelled) return;
        toFetch.forEach((key, i) => known.set(key, results[i]));
        batch.forEach((key) => scanned.set(key, known.get(key)));
        writeSeriesCache(cacheKey, known, cachedAt);
        publish({ loading: true, complete: false, failed: false });
        // eslint-disable-next-line no-await-in-loop
        if (toFetch.length > 0) await sleep(PAUSE_MS);
      }
    };

    const run = async () => {
      setSeries({ months: [], loading: true, complete: false, failed: false });
      try {
        // eslint-disable-next-line no-restricted-syntax
        for (const range of yearRanges(start, end)) {
          if (cancelled || reachedTotal()) break;
          // eslint-disable-next-line no-await-in-loop
          await scanYear(range);
        }
        if (cancelled) return;
        writeSeriesCache(cacheKey, known, cachedAt);
        publish({ loading: false, complete: reachedTotal(), failed: false });
      } catch (err) {
        if (cancelled) return;
        console.error("Erro ao carregar a evolução mensal:", err);
        publish({ loading: false, complete: false, failed: true });
      }
    };

    run();
    return () => { cancelled = true; };
  }, [project, empresa, dataInicio, dataFim, total, ready, reloadKey]);

  return series;
}

// Fornecedores por chave (projeto + tipos + reloadKey), para voltar à tela sem refazer as chamadas
const supplierCache = new Map();

/**
 * Ranking de fornecedores: uma chamada a /creditos/agrupado por tipo de crédito.
 * Essa rota não filtra por data, logo o período selecionado na tela não muda
 * este resultado (a tela avisa isso). O filtro de empresa é aplicado aqui.
 */
export function useSuppliers({ project, creditos, empresa, ready, reloadKey }) {
  const [state, setState] = useState({ suppliers: [], loading: false, failed: false });

  // Só refaz as chamadas se projeto, tipos ou reloadKey mudarem: uma troca de
  // período recarrega a lista (ready oscila), mas não muda este resultado.
  const tipos = useMemo(() => creditos.map((c) => c.tipoCredito).filter(Boolean).sort().join("|"), [creditos]);
  const creditosRef = useRef(creditos);
  creditosRef.current = creditos;
  const requestKey = useRef("");
  if (ready) requestKey.current = project && tipos ? `${project}|${tipos}|${reloadKey}` : "";
  const key = requestKey.current;

  useEffect(() => {
    if (!key) {
      setState({ suppliers: [], loading: false, failed: false });
      return undefined;
    }

    const cached = supplierCache.get(key);
    if (cached && Date.now() - cached.at < CACHE_TTL_MS) {
      setState({ suppliers: cached.suppliers, loading: false, failed: false });
      return undefined;
    }

    let cancelled = false;
    const isCancelled = () => cancelled;
    setState((prev) => ({ ...prev, loading: true, failed: false }));

    const run = async () => {
      const settled = [];
      const pending = [...creditosRef.current];
      while (pending.length > 0 && !cancelled) {
        const batch = pending.splice(0, CONCURRENCY);
        // eslint-disable-next-line no-await-in-loop
        const results = await Promise.allSettled(batch.map((credito) => withRetry(async () => {
          const { data } = await api.get(`/creditos/agrupado/${credito.tipoCredito}`, { params: { projeto: project } });
          if (!data.success) throw new Error("Resposta sem sucesso");
          return { credito, fornecedores: data.data?.fornecedores || [] };
        }, isCancelled)));
        settled.push(...results);
        // eslint-disable-next-line no-await-in-loop
        if (pending.length > 0) await sleep(PAUSE_MS);
      }
      if (cancelled) return;
      const groups = settled.filter((r) => r.status === "fulfilled").map((r) => r.value);
      const failed = groups.length < settled.length;
      const suppliers = buildSuppliers(groups);
      if (!failed) supplierCache.set(key, { at: Date.now(), suppliers });
      setState({ suppliers, loading: false, failed });
    };

    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const suppliers = useMemo(
    () => (empresa ? state.suppliers.filter((s) => s.cnpj === empresa) : state.suppliers),
    [state.suppliers, empresa]
  );

  return { ...state, suppliers };
}
